import React from 'react';
import styled from 'styled-components';
import ModelVideo from './ModelVideo';
import videoSrc from '../../images/lunex_model/static_direct.mp4';
import exampleInstall from '../../images/solar.JPG';
import { SERVED_STATES } from '../../constants/servedStates';

/**
 * The left half of the intake flow.
 *
 * It used to be the same looping model video on every screen, which told a
 * first-time visitor nothing — and this traffic arrives cold from a link on
 * lunexpower.com calling it a "self service pricing tool". The customer is
 * asking a different question on each of the three screens, so the panel
 * answers a different one on each:
 *
 *   locate   what is this, and is somebody going to call me?
 *   contact  why do you need my phone number?
 *   otp      is anything actually happening?
 *
 * The `otp` panel is the reason this exists at all. The estimate call finishes
 * before that screen renders, so the customer's own roof and their estimated
 * system size are already in state — and were being covered by a video.
 */

/* ------------------------------------------------------------------ shell */

const Panel = styled.aside`
  max-width: 720px;
  margin: 70px 0 10px 12px;
  padding: 6px 26px 0 4px;
  max-height: calc(100vh - 100px);
  overflow-y: auto;
  align-self: start;

  @media screen and (max-width: 900px) {
    width: 95%;
    max-width: 460px;
    margin: 60px auto 0;
    /* Symmetric, so the hairline rules under each step stay balanced. The 95%
       width alone leaves only ~10px of gutter on a phone, which is not enough
       for the video tile on the right — it is a solid dark block with a 20px
       shadow, so it reads as touching the edge where text at the same inset
       does not. */
    padding: 0 12px;
    max-height: none;
    overflow: visible;
  }
`;

const Head = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) 212px;
  gap: 22px;
  align-items: start;

  @media screen and (max-width: 900px) {
    grid-template-columns: minmax(0, 1fr) 132px;
    gap: 16px;
  }

  @media screen and (max-width: 560px) {
    grid-template-columns: minmax(0, 1fr) 116px;
    gap: 12px;
  }
`;

const Kicker = styled.div`
  font-size: 0.62rem;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  font-weight: 800;
  color: #8098a8;
  margin-bottom: 10px;
`;

const Title = styled.h2`
  font-size: 1.8rem;
  line-height: 1.1;
  letter-spacing: -0.01em;
  font-weight: 800;
  color: #0f1b24;
  margin: 0;

  @media screen and (max-width: 900px) {
    font-size: 1.4rem;
  }

  @media screen and (max-width: 560px) {
    font-size: 1.15rem;
  }
`;

/* ------------------------------------------------------------- step 1 list */

const Steps = styled.ol`
  list-style: none;
  margin: 26px 0 0;
  padding: 0;
  border-top: 1px solid #eaf0f4;
`;

const Step = styled.li`
  border-bottom: 1px solid #eaf0f4;
  padding: 16px 0;
  display: grid;
  grid-template-columns: 30px minmax(0, 1fr) auto;
  gap: 14px;
  align-items: baseline;

  @media screen and (max-width: 900px) {
    padding: 11px 0;
  }
`;

const StepNumber = styled.span`
  font-size: 0.8rem;
  font-weight: 800;
  color: #1ba8f1;
  border: 1px solid #1ba8f1;
  border-radius: 50%;
  width: 26px;
  height: 26px;
  display: flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
`;

const StepTitle = styled.span`
  display: block;
  font-size: 0.95rem;
  font-weight: 800;
  color: #0f1b24;
`;

/**
 * Descriptions and the sample quote go away on a phone. The panel sits above
 * the form when the grid collapses to one column, so anything kept here pushes
 * the address field further down the page.
 */
const StepDetail = styled.span`
  display: block;
  font-size: 0.83rem;
  line-height: 1.5;
  color: #41545f;
  margin-top: 3px;
  max-width: 46ch;

  @media screen and (max-width: 900px) {
    display: none;
  }
`;

const StepWhen = styled.span`
  font-size: 0.6rem;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  font-weight: 800;
  color: #8098a8;
  white-space: nowrap;
`;

/* ---------------------------------------------------------- example quote */

const Example = styled.div`
  position: relative;
  border: 1px solid #e0e6ea;
  border-radius: 12px;
  overflow: hidden;
  background: #fff;
  margin-top: 14px;
  max-width: 400px;

  @media screen and (max-width: 900px) {
    max-width: 100%;
    margin-top: 12px;
  }
`;

const ExampleImage = styled.img`
  display: block;
  width: 100%;
  height: 132px;
  object-fit: cover;

  @media screen and (max-width: 900px) {
    height: 104px;
  }
`;

/* The figures below are one real system, not this customer's. Say so on the
   card rather than in fine print somewhere else. */
const ExampleTag = styled.span`
  position: absolute;
  top: 9px;
  left: 9px;
  background: rgba(15, 27, 36, 0.78);
  color: #fff;
  font-size: 0.55rem;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  font-weight: 800;
  border-radius: 4px;
  padding: 4px 8px;
`;

const ExampleBody = styled.div`
  padding: 12px 14px;
`;

const ExampleSpecs = styled.div`
  font-size: 0.75rem;
  color: #8098a8;

  b {
    color: #0f1b24;
    font-weight: 800;
    font-size: 0.85rem;
  }
`;

const ExamplePrice = styled.div`
  display: flex;
  align-items: baseline;
  gap: 14px;
  margin-top: 9px;
  padding-top: 10px;
  border-top: 1px solid #eaf0f4;
`;

const ExampleTotal = styled.span`
  font-size: 1.4rem;
  font-weight: 800;
  letter-spacing: -0.02em;
  color: #0f1b24;
  font-variant-numeric: tabular-nums;
`;

const ExampleMonthly = styled.span`
  margin-left: auto;
  font-size: 0.78rem;
  color: #8098a8;
  font-variant-numeric: tabular-nums;

  b {
    font-weight: 800;
    color: #0f1b24;
    font-size: 0.92rem;
  }
`;

/* --------------------------------------------------------------- step 2/3 */

const Reasons = styled.ul`
  list-style: none;
  margin: 24px 0 0;
  padding: 0;
  border-top: 1px solid #eaf0f4;
`;

const Reason = styled.li`
  border-bottom: 1px solid #eaf0f4;
  padding: 15px 0;
`;

const ReasonTitle = styled.span`
  display: block;
  font-size: 0.9rem;
  font-weight: 800;
  color: #0f1b24;
`;

const ReasonDetail = styled.span`
  display: block;
  font-size: 0.83rem;
  line-height: 1.5;
  color: #41545f;
  margin-top: 3px;
  max-width: 46ch;
`;

const Callout = styled.div`
  margin-top: 20px;
  padding: 13px 15px;
  border: 1px solid #e0e6ea;
  border-radius: 10px;
  background: #f6fafd;
  font-size: 0.83rem;
  line-height: 1.5;
  color: #41545f;

  b {
    color: #0f1b24;
    font-weight: 800;
    display: block;
    margin-bottom: 2px;
  }
`;

const RoofImage = styled.img`
  display: block;
  width: 100%;
  height: 240px;
  object-fit: cover;
  border: 1px solid #e0e6ea;
  border-radius: 12px;

  @media screen and (max-width: 900px) {
    height: 170px;
  }
`;

const RoofMissing = styled.div`
  height: 240px;
  border: 1px dashed #e0e6ea;
  border-radius: 12px;
  background: #f6fafd;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  text-align: center;
  padding: 20px;

  @media screen and (max-width: 900px) {
    height: 150px;
  }
`;

const RoofMissingTitle = styled.span`
  font-size: 0.9rem;
  font-weight: 800;
  color: #0f1b24;
`;

const RoofMissingDetail = styled.span`
  font-size: 0.8rem;
  color: #8098a8;
  max-width: 34ch;
  line-height: 1.5;
`;

const RoofAddress = styled.div`
  font-size: 0.78rem;
  color: #8098a8;
  margin-top: 9px;
`;

const Stats = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  border-top: 1px solid #eaf0f4;
  margin-top: 18px;
`;

const Stat = styled.div`
  padding: 13px 12px 13px 0;
  border-bottom: 1px solid #eaf0f4;
`;

const StatValue = styled.span`
  display: block;
  font-size: 1.3rem;
  font-weight: 800;
  line-height: 1.1;
  color: #0f1b24;
  font-variant-numeric: tabular-nums;

  small {
    font-size: 0.62rem;
    font-weight: 800;
  }
`;

const StatKey = styled.span`
  display: block;
  font-size: 0.6rem;
  letter-spacing: 0.13em;
  text-transform: uppercase;
  font-weight: 800;
  color: #8098a8;
  margin-top: 5px;
`;

const Footnote = styled.p`
  font-size: 0.78rem;
  color: #8098a8;
  margin: 12px 0 0;
  line-height: 1.5;
`;

/* ---------------------------------------------------------------- states */

const StatesRow = styled.div`
  display: flex;
  align-items: baseline;
  gap: 10px;
  flex-wrap: wrap;
  margin-top: 20px;
  padding-top: 12px;
  border-top: 1px solid #eaf0f4;
`;

const StatesKey = styled.span`
  font-size: 0.58rem;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  font-weight: 800;
  color: #8098a8;
`;

const StatesValue = styled.span`
  font-size: 0.75rem;
  letter-spacing: 0.06em;
  font-weight: 800;
  color: #41545f;
`;

/* ------------------------------------------------------------------ steps */

const LocatePanel = () => (
  <>
    <Head>
      <div>
        <Kicker>Price your own solar</Kicker>
        {/* Not "no home visit" — a site survey is scheduled once someone
            signs, and the success panel says so. What is actually true is that
            nobody has to come out to sell it and nothing has to be waited on. */}
        <Title>
          No salesperson.
          <br />
          No waiting for a quote.
        </Title>
      </div>
      <ModelVideo videoSrc={videoSrc} playbackSpeed={0.4} compact />
    </Head>

    <Steps>
      <Step>
        <StepNumber>1</StepNumber>
        <span>
          <StepTitle>Two details about your home</StepTitle>
          <StepDetail>Your address, and roughly what you pay for power.</StepDetail>
        </span>
        <StepWhen>30 sec</StepWhen>
      </Step>
      <Step>
        <StepNumber>2</StepNumber>
        <span>
          <StepTitle>We design your actual roof</StepTitle>
          <StepDetail>
            Satellite imagery and real engineering — not a rule of thumb off your bill.
          </StepDetail>
        </span>
        <StepWhen>1 min</StepWhen>
      </Step>
      <Step>
        <StepNumber>3</StepNumber>
        <span>
          <StepTitle>Then pick your package</StepTitle>
          <StepDetail>
            Four of them, from streamlined solar to full home battery backup. Change anything
            and the price moves with it.
          </StepDetail>
          <Example>
            <ExampleImage src={exampleInstall} alt="A finished Lunex installation" />
            <ExampleTag>Example</ExampleTag>
            <ExampleBody>
              <ExampleSpecs>
                <b>11.38 kW</b> · 25 panels · 19.85 kWh battery
              </ExampleSpecs>
              <ExamplePrice>
                <ExampleTotal>$27,044</ExampleTotal>
                <ExampleMonthly>
                  <b>$206</b>/mo
                </ExampleMonthly>
              </ExamplePrice>
            </ExampleBody>
          </Example>
        </span>
      </Step>
    </Steps>

    <StatesRow>
      <StatesKey>Available in</StatesKey>
      <StatesValue>{SERVED_STATES.join(' · ')}</StatesValue>
    </StatesRow>
  </>
);

const ContactPanel = () => (
  <>
    <Kicker>Why we ask</Kicker>
    <Title>
      Three things,
      <br />
      and what each one is for.
    </Title>

    <Reasons>
      <Reason>
        <ReasonTitle>The code goes to your email and phone</ReasonTitle>
        <ReasonDetail>So nobody but you can pull up a price for your address.</ReasonDetail>
      </Reason>
      {/* This used to promise the design was saved to the customer's email and
          could be picked up tomorrow. Nothing persists the flow — a refresh
          loses it — and the one-time code is the only mail this flow sends a
          customer. It also promised nobody would call, on the very screen that
          creates a Coperniq contact and notifies four people at Lunex. */}
      <Reason>
        <ReasonTitle>So we can reach you about this quote</ReasonTitle>
        <ReasonDetail>If your roof needs a closer look, this is how we get in touch.</ReasonDetail>
      </Reason>
      <Reason>
        <ReasonTitle>We don't sell your details</ReasonTitle>
        <ReasonDetail>They stay inside Lunex. We don't pass them to anyone else.</ReasonDetail>
      </Reason>
    </Reasons>

    <Callout>
      <b>Next</b>
      We pull up your roof and size a system from your bill.
    </Callout>
  </>
);

const round = (value) => Math.round(Number(value) || 0);

/**
 * The estimate is already in state by the time this renders, so show it.
 *
 * Imagery is the part that can be missing — the satellite fetch is deliberately
 * non-fatal and Google Solar has no coverage for some addresses — so the panel
 * has to read properly without it rather than leaving a broken frame.
 */
const OtpPanel = ({ satelliteImageUrl, address, estimateSize, solarProductionMonthly }) => {
  const kw = (Number(estimateSize) || 0) / 1000;
  const annual = round(Number(solarProductionMonthly) || 0) * 12;
  const hasImage = Boolean(satelliteImageUrl);

  return (
    <>
      <Kicker>We found your home</Kicker>
      <Title>{hasImage ? 'Here it is.' : "We're sizing it now."}</Title>

      <div style={{ marginTop: '18px' }}>
        {hasImage ? (
          <RoofImage src={satelliteImageUrl} alt="Satellite view of your home" />
        ) : (
          <RoofMissing>
            <RoofMissingTitle>No clear image of this roof</RoofMissingTitle>
            <RoofMissingDetail>
              Aerial imagery isn't available for every address. It doesn't affect your design — we
              build that from the roof itself.
            </RoofMissingDetail>
          </RoofMissing>
        )}
        {address && <RoofAddress>{address}</RoofAddress>}
      </div>

      {kw > 0 && (
        <>
          <Stats>
            <Stat>
              <StatValue>
                ~{kw.toFixed(1)} <small>kW</small>
              </StatValue>
              <StatKey>Estimated system</StatKey>
            </Stat>
            <Stat>
              <StatValue>~{annual.toLocaleString('en-US')}</StatValue>
              <StatKey>kWh a year</StatKey>
            </Stat>
          </Stats>
          <Footnote>
            Sized from the bill you gave us. Your real design — panel by panel — comes next.
          </Footnote>
        </>
      )}

      <Callout>
        <b>Didn't get the code?</b>
        Check your spam folder. It expires ten minutes after we send it.
      </Callout>
    </>
  );
};

const StoryPanel = ({
  step,
  satelliteImageUrl,
  address,
  estimateSize,
  solarProductionMonthly,
}) => (
  <Panel>
    {step === 'contact' && <ContactPanel />}
    {step === 'otp' && (
      <OtpPanel
        satelliteImageUrl={satelliteImageUrl}
        address={address}
        estimateSize={estimateSize}
        solarProductionMonthly={solarProductionMonthly}
      />
    )}
    {step !== 'contact' && step !== 'otp' && <LocatePanel />}
  </Panel>
);

export default StoryPanel;
